import { Home, Store, HardHat, Users, Eye } from "lucide-react";
import { useState } from "react";
import { useLanguage } from "../../context/LanguageContext";

const INTERIOR_1 = "/assets/images/interior-6.webp";
const INTERIOR_2 = "/assets/images/interior-8.webp";
const INTERIOR_3 = "/assets/images/interior-4.webp";
const INTERIOR_4 = "/assets/images/interior-3.webp";

const interiorImages = [INTERIOR_1, INTERIOR_2, INTERIOR_3, INTERIOR_4];
const statIcons = [Home, Store, HardHat, Users];

const tours = [
  { url: "https://momento360.com/e/u/c0a7029a80ad4b529e99cb12849a335a?utm_campaign=embed&utm_source=other&heading=524&pitch=-9.28&field-of-view=100&size=medium&display-plan=true" },
  { url: "https://momento360.com/e/u/487bc0a87b5b4b07915d638e4eab76ce?utm_campaign=embed&utm_source=other&heading=399.28&pitch=-5.51&field-of-view=75&size=medium" },
  { url: "https://momento360.com/e/u/f49f075a1d004e90b4a9deab7ea0e01a?utm_campaign=embed&utm_source=other&heading=691.53&pitch=-12.14&field-of-view=81.38&size=medium" },
  { url: "https://momento360.com/e/u/c6d910892ed442a4a56a0f8e86db4e6a?utm_campaign=embed&utm_source=other&heading=256.99&pitch=-17.16&field-of-view=75&size=medium" },
];

const DevelopmentSection = () => {
  const [activeTour, setActiveTour] = useState(0);
  const { t } = useLanguage();

  return (
    <section className="bg-white" data-testid="development-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
        <span className="text-xs uppercase tracking-[0.3em] text-[#B5613B] font-accent mb-4 block text-center">{t.development.label}</span>
        <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-[#2D2926] text-center mb-8" data-testid="development-title">{t.development.title}</h2>
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <p className="text-[#6B6560] leading-relaxed mb-4">{t.development.p1}</p>
          <p className="text-[#6B6560] leading-relaxed">{t.development.p2}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {t.development.stats.map((item, index) => {
            const Icon = statIcons[index];
            return (
              <div key={index} className="stat-card-light p-6 text-center" data-testid={`development-highlight-${index}`}>
                <Icon className="w-8 h-8 text-[#B5613B] mx-auto mb-3" />
                <span className="font-heading text-3xl text-[#2D2926] block mb-1">{item.value}</span>
                <span className="text-xs text-[#6B6560] uppercase tracking-wider">{item.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4">
        {interiorImages.map((src, index) => (
          <div key={index} className="relative overflow-hidden group aspect-square" data-testid={`development-interior-${index}`}>
            <img src={src} alt={t.development.interiors[index]} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" decoding="async" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
              <span className="text-white text-sm font-medium tracking-wide">{t.development.interiors[index]}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-[#FAF8F5] py-16 md:py-20" data-testid="virtual-tours" id="tour360">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Eye className="w-5 h-5 text-[#B5613B]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#B5613B] font-accent">{t.development.tourLabel}</span>
          </div>
          <h3 className="font-heading text-2xl md:text-3xl text-[#2D2926] text-center mb-10">{t.development.tourTitle}</h3>
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {t.development.tourTabs.map((label, index) => (
              <button key={index} onClick={() => setActiveTour(index)} data-testid={`tour-tab-${index}`}
                className={`px-6 py-3 text-sm uppercase tracking-wider font-medium transition-all duration-300 ${activeTour === index ? "bg-[#B5613B] text-white" : "bg-white text-[#6B6560] hover:text-[#2D2926] border border-[#B5613B]/20"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="aspect-[16/9] max-w-5xl mx-auto shadow-xl">
            <iframe src={tours[activeTour].url} style={{ width: "100%", height: "100%", border: "none" }} allowFullScreen title={`360° Tour - ${t.development.tourTabs[activeTour]}`} data-testid="tour-iframe" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default DevelopmentSection;
